import { useEffect, useRef } from 'react';
import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@libs/errors';
import { SocketInternalEvents } from '@libs/socket-signalr';

import { useAuth } from '@features/auth/hooks/useAuth';
import { AuthService } from '@features/auth/services/AuthService';
import { useRouteClubId } from '@features/club/hooks/useRouteClubId';

import { CLUB_STREAM_SOCKET_URL } from '../streams/clients';
import { components, GET, POST, USER_SOCKET_URL, UserSocket, UserSocketEvents } from './clients';

export const QUERY_KEYS = {
  nextRounds: (userId?: string) => ['NEXT_ROUNDS', userId],
};

/*
    ____,-------------------------------,____
    \   |             REST              |   /
    /___|-------------------------------|___\
*/

export function useLoginMutation() {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ login, password }: { login: string; password: string }) => {
      try {
        const res = (
          await POST('/api/v1/auth/by-password', {
            body: {
              username: login,
              password,
            },
          })
        ).data!;
        const { accessToken, user } = res;
        onLoginSuccess(ql, accessToken, user);
        return { success: true };
      } catch (e: unknown) {
        if (e instanceof ApiError && e.isUnauthorized) {
          return { success: false };
        }
        throw e;
      }
    },
    onError: report,
  });
}

export function useCrossLoginMutation() {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      return (
        await POST('/api/v1/auth/cross-login', {
          params: { query: { token } },
        })
      ).data!;
    },
    onSuccess({ accessToken, user }) {
      onLoginSuccess(ql, accessToken, user);
    },
    onError: report,
  });
}

export function useTokenRefreshMutation() {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async () => {
      // sync tabs
      AuthService.instance.syncFromLocalStorage();
      const status = AuthService.instance.status;
      if (status === 'authed') {
        return { success: true };
      }
      if (status === 'no-auth') {
        throw new Error('No token to refresh');
      }

      const res = (await POST('/api/v1/auth/refresh')).data!;
      const { accessToken, user } = res;
      onLoginSuccess(ql, accessToken, user);
      return { success: true };
    },
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;

      // повторять только 500тки
      return !(error instanceof ApiError && error.httpCode && error.httpCode < 500);
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    onError: report,
  });
}

export function useTokenLoginMutation() {
  const ql = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      return (
        await POST('/api/v1/auth/by-token', {
          params: { query: { token } },
        })
      ).data!;
    },
    onSuccess({ accessToken, user }) {
      onLoginSuccess(ql, accessToken, user);
    },
    onError: report,
  });
}

export function useMeQuery({ enabled } = { enabled: true }) {
  return useQuery({
    enabled,
    queryKey: ['/api/v1/user/me'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const data = (await GET('/api/v1/user/me')).data!;

      AuthService.instance.updateUserOnly(data);

      return data;
    },
  });
}

function onLoginSuccess(
  ql: QueryClient,
  accessToken: string,
  user: components['schemas']['UserDto'],
) {
  // Инвалидация кэша при логине. При разлогине кэш инвалидирует AuthProvider
  ql.invalidateQueries();

  updateCurrentUserQuery(ql, user);
  AuthService.instance.saveTokens(accessToken, user);
}

function updateCurrentUserQuery(client: QueryClient, data: components['schemas']['UserDto']) {
  client.setQueryData(['/api/v1/user/me'], data);
}

export function useNextGamesQuery({
  enabled,
  refetchInterval,
}: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    enabled,
    refetchInterval,
    queryKey: ['/api/v1/user/me/games', AuthService.instance.user?.userId],
    queryFn: async () => {
      return (await GET('/api/v1/user/me/games')).data!;
    },
  });
}

export function useNextRoundsQuery({
  enabled,
  refetchInterval,
}: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    enabled,
    refetchInterval,
    refetchOnWindowFocus: false,
    queryKey: QUERY_KEYS.nextRounds(AuthService.instance.user?.userId),
    queryFn: async () => {
      return (await GET('/api/v1/user/me/next-rounds')).data!;
    },
  });
}

export function useTournamentGamesQuery(
  clubId: string,
  tournamentId: string,
  roundNumber: number,
  { enabled } = { enabled: true },
) {
  return useQuery({
    enabled,
    refetchOnWindowFocus: false,
    queryKey: [
      '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/rounds/{roundNumber}/games',
      clubId,
      tournamentId,
      roundNumber,
    ],
    queryFn: async () => {
      return (
        await GET(
          '/api/v1/user/clubs/{clubId}/tournaments/{tournamentId}/rounds/{roundNumber}/games',
          {
            params: {
              path: {
                clubId,
                tournamentId,
                roundNumber,
              },
            },
          },
        )
      ).data!;
    },
  });
}

/*
    ____,-------------------------------,____
    \   |            Sockets            |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// User

let globalUserSocket: UserSocket | null = null;
let globalUserSocketSubscribers = 0;

export function useUserSocket(cb: (e: UserSocketEvents | SocketInternalEvents) => void) {
  const clubId = useRouteClubId();
  const { isAuthenticated } = useAuth();
  const ref = useRef(cb);
  ref.current = cb;

  const url = (() => {
    if (isAuthenticated) {
      return USER_SOCKET_URL;
    }

    if (clubId) {
      return `${CLUB_STREAM_SOCKET_URL}?clubId=${clubId}`;
    }

    return null;
  })();

  useEffect(() => {
    if (!url) return;

    if (!globalUserSocket || globalUserSocket.url !== url) {
      globalUserSocket?.discard();
      globalUserSocket = new UserSocket(url);
      globalUserSocketSubscribers = 0;
    }

    globalUserSocketSubscribers += 1;
    const unsubscribe = globalUserSocket.subscribe((e) => ref.current(e));

    return () => {
      unsubscribe();
      globalUserSocketSubscribers -= 1;
      if (globalUserSocketSubscribers <= 0) {
        globalUserSocket?.discard();
        globalUserSocket = null;
        globalUserSocketSubscribers = 0;
      }
    };
  }, [url]);
}
